import { useState } from 'react';
import { createContainer } from 'unstated-next';
import type { PAYMENT_PERIOD } from '../components/subscription-change-modal/components/payment/payment-selection';
import {
  ActionFlow,
  ICreditCard,
  IPlan,
  PlanType,
  ISaveWorkspaceDowngrade,
  STEPS,
  ICoupon,
} from '../components/subscription-change-modal/constant';

export interface IBillingContainer {
  showModal: boolean;
  currentPage: string;
  currentStep: string;
  isCreditCardAvailable: boolean;
  temporaryCreditCard: ICreditCard;
  isTemporaryCardValid: boolean;
  selectedCardForPayment: ICreditCard;
  isAnnualPaymentSelected: boolean;
  isCouponApplied: boolean;
  selectedPlan: PlanType | undefined;
  pricingPlan: IPlan | undefined;
  defaultUserPlan: IPlan | undefined;
  creditCard: ICreditCard[];
  currrentPaymentPlan: PAYMENT_PERIOD;
  actionFlow: ActionFlow;
  isActionRequirementOK: boolean;
  preserveDataForDowngrade: ISaveWorkspaceDowngrade;
  creditCardRef: any;
  coupons: ICoupon[];
  discountAmount: number;
  successFromUpdateCard: boolean;
  fromOnboarding: boolean;
  subscriptionCancelled: boolean;
}

const initialState: IBillingContainer = {
  showModal: false,
  currentPage: STEPS.SELECT_PLAN,
  currentStep: '',
  pricingPlan: undefined,
  defaultUserPlan: undefined,
  isCreditCardAvailable: false,
  isAnnualPaymentSelected: false,
  selectedPlan: undefined,
  creditCard: [],
  currrentPaymentPlan: 'monthly',
  selectedCardForPayment: {
    cardId: '',
    cardNumber: '',
    name: '',
    expirationDate: { year: '', month: '' },
    cvv: '',
  },
  temporaryCreditCard: {
    cardId: '',
    cardNumber: '',
    name: '',
    expirationDate: { year: '', month: '' },
    cvv: '',
  },
  isTemporaryCardValid: false,
  actionFlow: 'none',
  isActionRequirementOK: false,
  preserveDataForDowngrade: {
    datasources: [],
    workspace: [],
  },
  creditCardRef: {},
  coupons: [],
  discountAmount: 0,
  isCouponApplied: false,
  successFromUpdateCard: false,
  fromOnboarding: false,
  subscriptionCancelled: false,
};

const useBillingHook = () => {
  const [state, setState] = useState<IBillingContainer>(initialState);

  const hideShowModal = () => {
    if (state.subscriptionCancelled) {
      setState(prevState => ({
        ...prevState,
        showModal: false,
      }));
      return;
    }
    setState(prevState => ({
      ...prevState,
      showModal: !prevState.showModal,
    }));
  };

  const setShowModal = (showModal: boolean) => {
    setState(prevState => ({
      ...prevState,
      showModal,
    }));
  };

  const setCurrentPage = (currentPage: string) => {
    setState(prevState => ({
      ...prevState,
      currentPage,
    }));
  };

  const setCurrentStep = (currentStep: string) => {
    setState(prevState => ({
      ...prevState,
      currentStep,
    }));
  };

  const setIsCreditCardAvailable = (isCreditCardAvailable: boolean) => {
    setState(prevState => ({
      ...prevState,
      isCreditCardAvailable,
    }));
  };

  const setIsAnnualPaymentSelected = (isAnnualPaymentSelected: boolean) => {
    setState(prevState => ({
      ...prevState,
      isAnnualPaymentSelected,
    }));
  };

  const setPlan = (selectedPlan: PlanType | undefined) => {
    setState(prevState => ({
      ...prevState,
      selectedPlan,
    }));
  };

  const setCreditCard = (creditCard: ICreditCard) => {
    setState(prevState => ({
      ...prevState,
      creditCard: [creditCard],
    }));
  };

  const setPricingPlan = (pricingPlan: IPlan | undefined) => {
    setState(prevState => ({
      ...prevState,
      pricingPlan,
    }));
  };

  const setCurrrentPaymentPlan = (currrentPaymentPlan: PAYMENT_PERIOD) => {
    setState(prevState => ({
      ...prevState,
      currrentPaymentPlan,
    }));
  };

  const setSelectedCardForPayment = (selectedCardForPayment: ICreditCard) => {
    setState(prevState => ({
      ...prevState,
      selectedCardForPayment,
    }));
  };

  const setTemporaryCreditCard = (temporaryCreditCard: ICreditCard) => {
    setState(prevState => ({
      ...prevState,
      temporaryCreditCard,
    }));
  };

  const resetTemporaryCard = () => {
    setState(prevState => ({
      ...prevState,
      temporaryCreditCard: {
        cardId: '',
        cardNumber: '',
        cvv: '',
        expirationDate: {
          day: '',
          month: '',
        },
        name: '',
      },
    }));
  };

  const setIsTemporaryCardValid = (isTemporaryCardValid: boolean) => {
    setState(prevState => ({
      ...prevState,
      isTemporaryCardValid,
    }));
  };

  const setActionFlow = (actionFlow: ActionFlow) => {
    setState(prevState => ({
      ...prevState,
      actionFlow,
    }));
  };

  const setDefaultUserPlan = (defaultUserPlan: IPlan) => {
    setState(prevState => ({
      ...prevState,
      defaultUserPlan,
    }));
  };

  const setIsActionRequirementOK = (isActionRequirementOK: boolean) => {
    setState(prevState => ({
      ...prevState,
      isActionRequirementOK,
    }));
  };

  const resetPreserveDataForDowngrade = () => {
    setState(prevState => ({
      ...prevState,
      preserveDataForDowngrade: {
        datasources: [],
        workspace: [],
      },
    }));
  };

  const setPreserveDataForDowngrade = (
    preserveDataForDowngrade: ISaveWorkspaceDowngrade
  ) => {
    setState(prevState => ({
      ...prevState,
      preserveDataForDowngrade,
    }));
  };

  const setCreditCardRef = (creditCardRef: any) => {
    setState(prevState => ({
      ...prevState,
      creditCardRef,
    }));
  };

  const resetBillingState = () => {
    setState(prevState => ({
      ...prevState,
      isActionRequirementOK: false,
      selectedPlan: undefined,
      isAnnualPaymentSelected: false,
      coupons: [],
      discountAmount: 0,
    }));
  };

  const hardResetBillingState = () => {
    setState(() => {
      return { ...initialState };
    });
  };

  const setCoupons = (coupons: ICoupon[]) => {
    setState(prevState => ({
      ...prevState,
      coupons,
    }));
  };

  const setDiscountAmount = (discountAmount: number) => {
    setState(prevState => ({
      ...prevState,
      discountAmount,
    }));
  };

  const setIsCouponApplied = (isCouponApplied: boolean) => {
    setState(prevState => ({
      ...prevState,
      isCouponApplied,
    }));
  };

  const setSuccessFromUpdateCard = (successFromUpdateCard: boolean) => {
    setState(prevState => ({
      ...prevState,
      successFromUpdateCard,
    }));
  };

  const setFromOnboarding = (fromOnboarding: boolean) => {
    setState(prevState => ({
      ...prevState,
      fromOnboarding,
    }));
  };

  const setSubscriptionCancelled = (subscriptionCancelled: boolean) => {
    setState(prevState => ({
      ...prevState,
      subscriptionCancelled,
    }));
  };
  return {
    state,
    hideShowModal,
    setCurrentPage,
    setCurrentStep,
    setIsCreditCardAvailable,
    setIsAnnualPaymentSelected,
    setPlan,
    setCreditCard,
    setPricingPlan,
    setCurrrentPaymentPlan,
    setTemporaryCreditCard,
    setSelectedCardForPayment,
    resetTemporaryCard,
    setIsTemporaryCardValid,
    setActionFlow,
    setDefaultUserPlan,
    setIsActionRequirementOK,
    setPreserveDataForDowngrade,
    resetPreserveDataForDowngrade,
    resetBillingState,
    setCreditCardRef,
    setCoupons,
    setDiscountAmount,
    hardResetBillingState,
    setIsCouponApplied,
    setSuccessFromUpdateCard,
    setFromOnboarding,
    setSubscriptionCancelled,
    setShowModal,
  };
};

const BillingContainer = createContainer(useBillingHook);

export default BillingContainer;
